import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, TrendingDown, PieChart } from 'lucide-react';
import api from '../lib/api';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface FinanceStats {
  totalReceived: number;
  totalPending: number;
  totalOverdue: number;
  netProfit: number;
}

interface Charge {
  id: string;
  competence: string;
  dueDate: string;
  amount: string | number;
  paidAmount?: string | number;
  status: string;
  paymentDate?: string;
  lease?: {
    code: string;
    property?: {
      title?: string;
      code: string;
    };
  };
}

export default function FinancePage() {
  const [stats, setStats] = useState<FinanceStats>({
    totalReceived: 0,
    totalPending: 0,
    totalOverdue: 0,
    netProfit: 0,
  });
  const [pendingCharges, setPendingCharges] = useState<Charge[]>([]);
  const [overdueCharges, setOverdueCharges] = useState<Charge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFinanceData();
  }, []);

  const loadFinanceData = async () => {
    try {
      const response = await api.get('/charges');
      const chargesData = response.data.data || [];

      // Calcular estatísticas financeiras
      const paidCharges = chargesData.filter((c: Charge) => c.status === 'PAID');
      const pendingChargesList = chargesData.filter((c: Charge) => c.status === 'PENDING');
      const overdueChargesList = chargesData.filter((c: Charge) => c.status === 'OVERDUE');

      const totalReceived = paidCharges.reduce(
        (sum: number, c: Charge) => sum + (parseFloat(c.amount as string) || 0),
        0
      );

      const totalPending = pendingChargesList.reduce(
        (sum: number, c: Charge) => sum + (parseFloat(c.amount as string) || 0),
        0
      );

      const totalOverdue = overdueChargesList.reduce(
        (sum: number, c: Charge) => sum + (parseFloat(c.amount as string) || 0),
        0
      );

      // Lucro líquido = total recebido (considerando taxa de administração já descontada)
      const netProfit = totalReceived;

      setStats({
        totalReceived,
        totalPending,
        totalOverdue,
        netProfit,
      });

      // Cobranças pendentes (ordenadas por vencimento)
      const sortedPending = [...pendingChargesList]
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
        .slice(0, 10);
      setPendingCharges(sortedPending);

      // Cobranças vencidas (ordenadas por vencimento)
      const sortedOverdue = [...overdueChargesList]
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
        .slice(0, 10);
      setOverdueCharges(sortedOverdue);

    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID':
        return 'Pago';
      case 'PENDING':
        return 'Pendente';
      case 'OVERDUE':
        return 'Vencido';
      case 'CANCELLED':
        return 'Cancelado';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Financeiro</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Receitas Recebidas</p>
              <p className="text-2xl font-bold mt-1 text-green-600">
                R$ {stats.totalReceived.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-gray-500 mt-2">Total pago</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">A Receber</p>
              <p className="text-2xl font-bold mt-1 text-yellow-600">
                R$ {stats.totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-gray-500 mt-2">Pendentes</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vencidas</p>
              <p className="text-2xl font-bold mt-1 text-red-600">
                R$ {stats.totalOverdue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-gray-500 mt-2">Em atraso</p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Lucro Líquido</p>
              <p className="text-2xl font-bold mt-1 text-primary-600">
                R$ {stats.netProfit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-gray-500 mt-2">Total recebido</p>
            </div>
            <div className="bg-primary-100 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-xl font-bold mb-6">Resumo Financeiro</h2>
          <div className="space-y-4">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Receitas Recebidas</span>
              <span className="font-semibold text-green-600">
                R$ {stats.totalReceived.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">A Receber (Pendentes)</span>
              <span className="font-semibold text-yellow-600">
                R$ {stats.totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Em Atraso (Vencidas)</span>
              <span className="font-semibold text-red-600">
                R$ {stats.totalOverdue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="flex justify-between py-3 pt-4 border-t-2 border-gray-300">
              <span className="font-bold text-lg">Total Esperado</span>
              <span className="font-bold text-lg text-primary-600">
                R$ {(stats.totalReceived + stats.totalPending + stats.totalOverdue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-xl font-bold mb-6">Distribuição</h2>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Recebidas</span>
                <span className="text-sm font-semibold">
                  {((stats.totalReceived / (stats.totalReceived + stats.totalPending + stats.totalOverdue || 1)) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${(stats.totalReceived / (stats.totalReceived + stats.totalPending + stats.totalOverdue || 1)) * 100}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Pendentes</span>
                <span className="text-sm font-semibold">
                  {((stats.totalPending / (stats.totalReceived + stats.totalPending + stats.totalOverdue || 1)) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-yellow-600 h-2 rounded-full"
                  style={{ width: `${(stats.totalPending / (stats.totalReceived + stats.totalPending + stats.totalOverdue || 1)) * 100}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Vencidas</span>
                <span className="text-sm font-semibold">
                  {((stats.totalOverdue / (stats.totalReceived + stats.totalPending + stats.totalOverdue || 1)) * 100).toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-red-600 h-2 rounded-full"
                  style={{ width: `${(stats.totalOverdue / (stats.totalReceived + stats.totalPending + stats.totalOverdue || 1)) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cobranças Vencidas (Prioridade) */}
      {overdueCharges.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold mb-6 text-red-600">⚠️ Cobranças Vencidas</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Imóvel</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Contrato</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Vencimento</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Valor</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {overdueCharges.map((charge) => (
                  <tr key={charge.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      {charge.lease?.property?.title || charge.lease?.property?.code || 'Imóvel'}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{charge.lease?.code || '-'}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {format(parseISO(charge.dueDate), 'dd/MM/yyyy', { locale: ptBR })}
                    </td>
                    <td className="py-3 px-4 font-semibold">
                      R$ {(parseFloat(charge.amount as string) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(charge.status)}`}>
                        {getStatusLabel(charge.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Contas a Receber (Pendentes) */}
      <div className="card">
        <h2 className="text-xl font-bold mb-6">Contas a Receber</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Imóvel</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Contrato</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Vencimento</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Valor</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {pendingCharges.length > 0 ? (
                pendingCharges.map((charge) => (
                  <tr key={charge.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      {charge.lease?.property?.title || charge.lease?.property?.code || 'Imóvel'}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{charge.lease?.code || '-'}</td>
                    <td className="py-3 px-4 text-gray-600">
                      {format(parseISO(charge.dueDate), 'dd/MM/yyyy', { locale: ptBR })}
                    </td>
                    <td className="py-3 px-4 font-semibold">
                      R$ {(parseFloat(charge.amount as string) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(charge.status)}`}>
                        {getStatusLabel(charge.status)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-gray-500">
                    Nenhuma cobrança pendente
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
